import type React from "react";
import { useRef, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  IconButton,
  useTheme,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import QrCode from "react-qr-code";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { tokens } from "../../../theme";
import { formatDateToLongString, formatISODateTime } from "../../../utils/FormatDate";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import CustomTextField from "../../../components/ui/Textfield";
import { useParams } from "react-router-dom";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

interface StreamData {
  id: number;
  website_stream_link?: string;
  booking: {
    id: number;
    contact?: {
      phone?: string;
    };
    package_item?: {
      name?: string;
    };
    deceased: Array<{
      first_name: string;
      second_name?: string;
      surname: string;
    }>;
    services?: Array<{
      venue?: {
        name: string;
      };
    }>;
  };
  date: string;
  address_one?: string;
  city?: string;
}

interface DeliveryNote {
  comments: string | null;
  confirmed_by_name: string | null;
  confirmed_by_position: string | null;
  signature: string | null;
  status: "pending" | "signed";
  signed_at: string | null;
}

const ServiceDeliveryNotePage: React.FC = () => {
  const { id } = useParams();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const printRef = useRef<HTMLDivElement>(null);
  const [loadingAction, setLoadingAction] = useState<
    "print" | "download" | null
  >(null);
  const [streamData, setStreamData] = useState<StreamData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchStreamData = async () => {
      try {
        const response = await apiService.get<StreamData>(
          `/api/streams/stream/${id}/`,
        );
        if (isMounted) {
          setStreamData(response.data);
          toast.success("Service delivery note loaded successfully.");
        }
      } catch (error) {
        console.error("Failed to fetch stream data", error);
        if (isMounted) {
          toast.error("Failed to load service delivery note.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (id) {
      fetchStreamData();
    }
    return () => {
      isMounted = false;
    };
  }, [id]);

  const [deliveryNote, setDeliveryNote] = useState<DeliveryNote | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchDeliveryNote = async () => {
      try {
        const response = await apiService.get<
          DeliveryNote[] | { results: DeliveryNote[] }
        >(`/api/streams/service-delivery-note/?stream=${id}`);
        const data = response.data;
        const note = Array.isArray(data) ? data[0] : data.results?.[0];
        if (isMounted) {
          setDeliveryNote(note ?? null);
        }
      } catch (error) {
        console.error("Failed to fetch service delivery note", error);
      }
    };

    if (id) {
      fetchDeliveryNote();
    }
    return () => {
      isMounted = false;
    };
  }, [id]);

  const isSigned = deliveryNote?.status === "signed";

  const [formData, setFormData] = useState({
    serviceProvider: "HELLO ZIMBABWE RESTAURANTS",
    bookingId: "",
    contract: "RETAINER AGREEMENT",
    oldMutualBranch: "Harare CBD",
    services: "Funeral Streaming Services",
    oldMutualRep: "Sarah Masunda",
    clientPhone: "",
    comments: "",
    confirmedByName: "",
    confirmedByPosition: "",
    confirmedDate: new Date().toLocaleDateString("en-GB"),
    signature: "",
  });

  // Update form data when stream data loads
  useEffect(() => {
    if (streamData) {
      setFormData((prev) => ({
        ...prev,
        bookingId: streamData.booking.id.toString(),
        clientPhone: streamData.booking.contact?.phone || "",
      }));
    }
  }, [streamData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const generatePDF = async (): Promise<jsPDF | null> => {
    if (!printRef.current) {
      toast.error("No content to generate");
      return null;
    }

    try {
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const canvas = await html2canvas(printRef.current, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
      return pdf;
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Document generation failed");
      return null;
    }
  };

  const handlePrint = async () => {
    setLoadingAction("print");
    try {
      const pdf = await generatePDF();
      if (!pdf) return;

      // Convert to blob and create print window
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Pop-up blocked! Allow pop-ups to print.");
        URL.revokeObjectURL(pdfUrl);
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print Service Delivery Note</title>
          <style>
            body { margin: 0; overflow: hidden; }
            iframe { width: 100vw; height: 100vh; border: none; }
          </style>
        </head>
        <body>
          <iframe src="${pdfUrl}" "></iframe>
        </body>
        </html>
      `);
      printWindow.document.close();

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
    } catch (error) {
      console.error("Print failed:", error);
      toast.error("Print preparation failed");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownload = async () => {
    setLoadingAction("download");
    try {
      const pdf = await generatePDF();
      if (pdf) {
        pdf.save(
          `Service-Delivery-Note-${streamData?.booking.deceased[0]?.first_name}-${streamData?.booking.deceased[0]?.surname}.pdf`,
        );
        toast.success("PDF downloaded successfully");
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleShare = async () => {
    if (navigator.share && streamData?.website_stream_link) {
      try {
        await navigator.share({
          title: "Service Delivery Note",
          text: `Service Delivery Note for ${streamData.booking.deceased[0]?.first_name} ${streamData.booking.deceased[0]?.surname}`,
          url: streamData.website_stream_link,
        });
      } catch (error) {
        console.error("Failed to share", error);
        toast.error("Sharing failed");
      }
    } else {
      toast.info("Sharing is not supported on this device");
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!streamData) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Typography>Stream data not found.</Typography>
      </motion.div>
    );
  }

  const deceasedName = streamData.booking.deceased
    ? [
        streamData.booking.deceased[0]?.first_name,
        streamData.booking.deceased[0]?.second_name,
        streamData.booking.deceased[0]?.surname,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const packageName = streamData.booking.package_item?.name || "N/A";
  const websiteUrl = streamData.website_stream_link || "";

  const labelStyle = {
    color: "#666",
    mb: 1,
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
  };

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#000000 !important",
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        color: colors.primary[400],
        width: "210mm",
        margin: "0 auto",
        minHeight: "297mm",
      }}
    >
      {/* Header Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        {isSigned && (
          <Chip
            icon={<CheckCircleIcon />}
            label={
              deliveryNote?.signed_at
                ? `Signed ${formatISODateTime(deliveryNote.signed_at)}`
                : "Signed"
            }
            color="success"
            variant="outlined"
            sx={{ mr: "auto" }}
          />
        )}
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
          <IconButton
            onClick={handlePrint}
            disabled={loadingAction === "print"}
            sx={{
              backgroundColor: colors.primary[800],
              color: colors.primary[100],
              width: 40,
              height: 40,
            }}
          >
            {loadingAction === "print" ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <PrintIcon />
            )}
          </IconButton>
        </motion.div>

        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
          <IconButton
            onClick={handleDownload}
            disabled={loadingAction === "download"}
            sx={{
              backgroundColor: colors.primary[800],
              color: colors.primary[100],
              width: 40,
              height: 40,
            }}
          >
            {loadingAction === "download" ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <DownloadIcon />
            )}
          </IconButton>
        </motion.div>

        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
          <IconButton
            onClick={handleShare}
            disabled
            // ={!websiteUrl}
            sx={{
              backgroundColor: colors.primary[800],
              color: colors.primary[100],
              width: 40,
              height: 40,
            }}
          >
            <ShareIcon />
          </IconButton>
        </motion.div>
      </Box>

      {/* Printable Content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 1,
          overflow: "hidden",
          maxWidth: 1200,
        }}
      >
        <Box ref={printRef} sx={{ p: 4, backgroundColor: "background.paper" }}>
          {/* Header with QR Code */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 4,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "text.primary" }}>
              Service Delivery Note
            </Typography>
            <Box sx={{ textAlign: "center" }}>
              <QrCode size={80} value={websiteUrl} />
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Service Provider Section */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Service Provider
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formData.serviceProvider}
                onChange={(e) =>
                  handleInputChange("serviceProvider", e.target.value)
                }
                sx={textFieldStyle}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Booking ID
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formData.bookingId}
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Contract
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formData.contract}
                onChange={(e) => handleInputChange("contract", e.target.value)}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Old Mutual Branch
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formData.oldMutualBranch}
                onChange={(e) =>
                  handleInputChange("oldMutualBranch", e.target.value)
                }
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Services
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formData.services}
                onChange={(e) => handleInputChange("services", e.target.value)}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Old Mutual Rep
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formData.oldMutualRep}
                onChange={(e) =>
                  handleInputChange("oldMutualRep", e.target.value)
                }
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Details Section */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
          >
            DETAILS
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Deceased Name
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={deceasedName}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Service Date
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formatDateToLongString(streamData.date || "")}
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Client Phone
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={formData.clientPhone}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Service Address
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={`${streamData.address_one || ""}, ${
                  streamData.city || ""
                }`}
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Venue
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={streamData.booking.services?.[0]?.venue?.name || "N/A"}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" sx={labelStyle}>
                Package
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={packageName}
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" sx={labelStyle}>
                Website URL
              </Typography>
              <CustomTextField
                fullWidth
                size="small"
                value={websiteUrl}
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Comments Section */}
          <Typography variant="caption" sx={labelStyle}>
            Comments / Observations
          </Typography>
          {isSigned ? (
            <Typography
              variant="body2"
              sx={{
                mb: 3,
                p: 1.5,
                border: "1px solid #000000",
                minHeight: 72,
                whiteSpace: "pre-wrap",
                color: "text.primary",
              }}
            >
              {deliveryNote?.comments || "—"}
            </Typography>
          ) : (
            <CustomTextField
              fullWidth
              multiline
              rows={3}
              value={formData.comments}
              onChange={(e) => handleInputChange("comments", e.target.value)}
              sx={{
                mb: 3,
                borderRadius: 0,
                ...textFieldStyle,
              }}
            />
          )}

          {/* Confirmation Section */}
          <Box
            sx={{
              border: "1px solid #000000",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Grid container>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    minHeight: 80,
                    borderRight: "1px solid #000000",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1, mt: -1.2, ml: -1 }}
                  >
                    CONFIRMED BY / NAME
                  </Typography>
                  {isSigned && (
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      {deliveryNote?.confirmed_by_name || "—"}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    minHeight: 80,
                    borderRight: "1px solid #000000",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1, mt: -1.2, ml: -1 }}
                  >
                    POSITION
                  </Typography>
                  {isSigned && (
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      {deliveryNote?.confirmed_by_position || "—"}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    minHeight: 80,
                    borderRight: "1px solid #000000",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1, mt: -1.2, ml: -1 }}
                  >
                    DATE
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", mt: "auto" }}
                  >
                    {isSigned && deliveryNote?.signed_at
                      ? new Date(deliveryNote.signed_at).toLocaleDateString("en-GB")
                      : new Date().toLocaleDateString("en-GB")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    minHeight: 80,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1, mt: -1.2, ml: -1 }}
                  >
                    SIGNATURE
                  </Typography>
                  {isSigned && deliveryNote?.signature ? (
                    <Box
                      component="img"
                      src={deliveryNote.signature}
                      alt="Signature"
                      sx={{ height: 32, objectFit: "contain", mt: "auto" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        borderBottom: "1px solid #000000",
                        mt: "auto",
                        mb: 0.5,
                      }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default ServiceDeliveryNotePage;
